import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Divider,
  Button,
  Stack,
  Paper
} from '@mui/material';
import { useAppDispatch } from 'src/redux/hooks';
import { showAlert } from 'src/redux/alert/alertReducer';
import { Helmet } from 'react-helmet-async';
import MainPageTitle from 'src/components/layout-components/main-layout/MainPageTitle';
import { DataService } from 'src/services/data-service';
import { IEmbassyInfo, DayOfWeek } from '.';
import Loader from 'src/components/Loader';
import EditIcon from '@mui/icons-material/Edit';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import AddEmbassyInfo from './add-embassy-info';

const daysOfWeek: DayOfWeek[] = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const EmbassyInfoListing: React.FC = () => {
  const dispatch = useAppDispatch();

  const [embassyInfo, setEmbassyInfo] = useState<IEmbassyInfo | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [refreshKey, setRefreshKey] = useState(0);
  const [openEditDialog, setOpenEditDialog] = useState<boolean>(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await DataService.get('api/EmbassyInfo/all');
      if (response.ok) {
        const res = await response.json();
        const list: IEmbassyInfo[] = res.data || [];

        // Get the first record
        if (list.length > 0) {
          setEmbassyInfo(list[0]);
        } else {
          setEmbassyInfo(null);
        }
      } else {
        dispatch(showAlert({ message: 'Error loading embassy info', type: 'error' }));
      }
    } catch (error) {
      dispatch(showAlert({ message: 'Error loading embassy info', type: 'error' }));
    } finally {
      setLoading(false);
    }
  }, [dispatch]);

  useEffect(() => {
    fetchData();
  }, [fetchData, refreshKey]);

  const handleEdit = () => {
    setOpenEditDialog(true);
  };

  const formatTime = (time: string) => {
    if (!time) return '';
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const renderDaySchedule = (day: DayOfWeek) => {
    if (!embassyInfo) return null;

    const dayKey = day.toLowerCase() as Lowercase<DayOfWeek>;
    const daySchedule = embassyInfo.weeklySchedule[dayKey];

    return (
      <Paper
        key={day}
        elevation={1}
        sx={{
          p: 2.5,
          borderLeft: 4,
          borderColor: daySchedule.open ? 'success.main' : 'error.main',
          transition: 'all 0.3s',
          '&:hover': {
            elevation: 3,
            transform: 'translateY(-2px)'
          }
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {day}
          </Typography>
          <Chip
            icon={daySchedule.open ? <CheckCircleIcon /> : <CancelIcon />}
            label={daySchedule.open ? 'Open' : 'Closed'}
            color={daySchedule.open ? 'success' : 'error'}
            size="small"
          />
        </Box>

        {daySchedule.open && daySchedule.windows.length > 0 ? (
          <Stack spacing={1.5}>
            {daySchedule.windows.map((window, index) => (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  p: 1.5,
                  bgcolor: 'background.default',
                  borderRadius: 1
                }}
              >
                <AccessTimeIcon fontSize="small" color="primary" />
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {formatTime(window.start)} - {formatTime(window.end)}
                </Typography>
              </Box>
            ))}
          </Stack>
        ) : (
          !daySchedule.open && (
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              Embassy is closed on {day}s
            </Typography>
          )
        )}
      </Paper>
    );
  };

  if (loading) {
    return <Loader />;
  }

  if (!embassyInfo) {
    return (
      <div>
        <Helmet>
          <title>Embassy Info Management</title>
        </Helmet>

        <MainPageTitle title={'Embassy Info Management'} />

        <Card sx={{ p: 4, mt: 2, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No embassy information available
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Please create embassy information to get started
          </Typography>
          <Button
            variant="contained"
            onClick={() => setOpenEditDialog(true)}
          >
            Create Embassy Info
          </Button>
        </Card>

        {openEditDialog && (
          <AddEmbassyInfo
            onSuccess={() => {
              setOpenEditDialog(false);
              setRefreshKey(prev => prev + 1);
            }}
            onClose={() => setOpenEditDialog(false)}
            embassyInfo={null}
          />
        )}
      </div>
    );
  }

  return (
    <div>
      <Helmet>
        <title>Embassy Info Management</title>
      </Helmet>

      <MainPageTitle
        title={'Embassy Weekly Schedule'}
        // action={{
        //   title: 'Update Schedule',
        //   onClick: handleEdit,
        // }}
      />

      <Card sx={{ p: 3, mt: 2 }}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
            Embassy Opening Hours
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Last updated: {new Date(embassyInfo.updatedAt).toLocaleString()}
          </Typography>
        </Box>

        <Divider sx={{ mb: 3 }} />

        <Grid container spacing={2}>
          {daysOfWeek.map(day => (
            <Grid item xs={12} sm={6} md={4} key={day}>
              {renderDaySchedule(day)}
            </Grid>
          ))}
        </Grid>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Embassy ID: {embassyInfo.id}
          </Typography>
          <Button
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={handleEdit}
          >
            Update Schedule
          </Button>
        </Box>
      </Card>

      {openEditDialog && (
        <AddEmbassyInfo
          onSuccess={() => {
            setOpenEditDialog(false);
            setRefreshKey(prev => prev + 1);
          }}
          onClose={() => setOpenEditDialog(false)}
          embassyInfo={embassyInfo}
        />
      )}
    </div>
  );
};

export default EmbassyInfoListing;
